import { captureSession } from '~/utils/sessionBoundary';
import { ConfirmationEvents } from './events';
import { ConfirmationContext, Scope } from './types';

export const setConfirmation = (scope: Scope): Partial<ConfirmationContext> => {
  const event = scope.event as Extract<ConfirmationEvents, { type: 'ON_SHOW_CONFIRMATION' }>;
  const originalOnConfirm = event.payload.onConfirm;
  // A dialog opened by one account must not run its action for the next one, even
  // if the dialog's closures were captured before the boundary.
  const session = captureSession();

  const result = {
    show: true,
    title: event.payload.title,
    message: event.payload.message,
    confirmLabel: event.payload.confirmLabel,
    // [MCV-1572] Default preserves every pre-existing caller's red/warning
    // presentation; only an explicit 'neutral' opts out.
    tone: event.payload.tone ?? 'destructive',
    // A callback from an ended session does nothing at all: it must not run the action,
    // and it must not close the dialog the NEXT session may be showing.
    onConfirm: () => {
      if (!session.isCurrent()) return;
      try {
        originalOnConfirm();
      } catch (error) {
        console.error('Error calling original onConfirm:', error);
      } finally {
        scope.self?.send({ type: 'ON_CLOSE_CONFIRMATION' });
      }
    },
    onCancel: () => {
      if (!session.isCurrent()) return;
      try {
        event.payload.onCancel?.();
      } finally {
        scope.self?.send({ type: 'ON_CLOSE_CONFIRMATION' });
      }
    },
  };

  return result;
};

export const resetConfirmation = (): Partial<ConfirmationContext> => {
  return {
    show: false,
    title: '',
    message: '',
    confirmLabel: 'Yes',
    tone: 'destructive' as const,
    onConfirm: () => undefined,
    onCancel: () => undefined,
  };
};
